import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Eye, 
  EyeOff, 
  Shield, 
  User, 
  Lock, 
  ArrowRight, 
  BarChart3, 
  Package, 
  ShieldCheck,
  Layers,
  Users,
  FileText,
  Wrench,
  Building2,
  Zap
} from 'lucide-react';
import { AxiosError } from 'axios';
import api from '@/services/api';

interface Branding {
  name: string;
  logo?: string;
  logoDark?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  tagline?: string;
  loginBackground?: string;
  loginOverlayColor?: string;
  loginOverlayOpacity?: number;
  loginWelcomeMessage?: string;
  footerText?: string;
}

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

const twoFASchema = z.object({
  token: z.string().length(6, 'Code must be 6 digits'),
});

type LoginFormData = z.infer<typeof loginSchema>;
type TwoFAFormData = z.infer<typeof twoFASchema>;

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [currentFeature, setCurrentFeature] = useState(0);
  const { login, verify2FA, requires2FA, pendingUserId } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();

  const features = [
    { icon: Package, title: 'Asset Lifecycle', desc: 'Track assets from procurement to disposal' },
    { icon: Users, title: 'Employee Management', desc: 'Assign and track assets by employee' },
    { icon: BarChart3, title: 'Real-time Analytics', desc: 'Dashboards, reports & depreciation tracking' },
    { icon: Wrench, title: 'Repair Workflow', desc: 'Service provider integration & repair tracking' },
    { icon: FileText, title: 'Document Management', desc: 'SLAs, contracts & compliance documents' },
    { icon: ShieldCheck, title: 'Enterprise Security', desc: 'Role-based access & audit trails' }
  ];

  // Rotate features every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  // Fetch branding
  const { data: branding } = useQuery<Branding>({
    queryKey: ['branding'],
    queryFn: async () => {
      try {
        const response = await api.get('/auth/branding');
        return response.data.data;
      } catch {
        return {
          name: 'Glide',
          tagline: 'Enterprise Asset Management',
          primaryColor: '#3B82F6',
        };
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Apply primary color as CSS variable
  useEffect(() => {
    if (branding?.primaryColor) {
      document.documentElement.style.setProperty('--login-primary', branding.primaryColor);
    }
  }, [branding]);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const twoFAForm = useForm<TwoFAFormData>({
    resolver: zodResolver(twoFASchema),
    defaultValues: {
      token: '',
    },
  });

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      
      const state = useAuthStore.getState();
      if (!state.requires2FA && state.isAuthenticated) {
        toast({
          title: 'Welcome back!',
          description: 'You have successfully logged in.',
        });
        navigate('/');
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      toast({
        title: 'Login failed',
        description: axiosError.response?.data?.error || 'Invalid email or password',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const on2FASubmit = async (data: TwoFAFormData) => {
    if (!pendingUserId) return;
    
    setIsLoading(true);
    try {
      await verify2FA(pendingUserId, data.token);
      toast({
        title: 'Welcome back!',
        description: 'You have successfully logged in.',
      });
      navigate('/');
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      toast({
        title: 'Verification failed',
        description: axiosError.response?.data?.error || 'Invalid verification code',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const apiUrl = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/api$/, '') : '';

  const getImageUrl = (path?: string): string | undefined => {
    if (!path) return undefined;
    if (path.startsWith('http')) return path;
    return `${apiUrl}${path}`;
  };

  const backgroundStyle = branding?.loginBackground ? {
    backgroundImage: `url(${getImageUrl(branding.loginBackground)})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  } : {};

  // 2FA Form
  if (requires2FA) {
    return (
      <div className="min-h-screen flex" style={backgroundStyle}>
        {branding?.loginBackground && (
          <div 
            className="absolute inset-0" 
            style={{ 
              backgroundColor: branding.loginOverlayColor || 'rgba(0,0,0,0.5)',
              opacity: branding.loginOverlayOpacity || 0.5 
            }} 
          />
        )}
        <div className="relative z-10 w-full flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              {branding?.logo && (
                <img 
                  src={getImageUrl(branding.logo)} 
                  alt={branding.name} 
                  className="h-16 mx-auto mb-4 object-contain"
                />
              )}
              <div className="mx-auto w-12 h-12 rounded-lg bg-primary flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
              <CardDescription>
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={twoFAForm.handleSubmit(on2FASubmit)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token">Verification Code</Label>
                  <Input
                    id="token"
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest"
                    maxLength={6}
                    {...twoFAForm.register('token')}
                    error={twoFAForm.formState.errors.token?.message}
                  />
                </div>

                <Button type="submit" className="w-full" loading={isLoading}>
                  Verify
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => useAuthStore.setState({ requires2FA: false, pendingUserId: null })}
                >
                  Back to login
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Login Form
  return (
    <div className="min-h-screen h-screen flex overflow-hidden bg-slate-950">
      {/* Left Panel - Glide Software Branding (NOT customer) */}
      <div 
        className="hidden lg:flex lg:w-[45%] flex-col relative overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)'
        }}
      >
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          {/* Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
          {/* Glowing Orbs */}
          <div 
            className="absolute -top-32 -left-32 w-96 h-96 rounded-full animate-pulse"
            style={{ 
              background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
              animationDuration: '4s'
            }}
          />
          <div 
            className="absolute -bottom-48 -right-48 w-[500px] h-[500px] rounded-full animate-pulse"
            style={{ 
              background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
              animationDuration: '6s',
              animationDirection: 'reverse'
            }}
          />
        </div>

        {/* Main Content - GLIDE ONLY */}
        <div className="relative z-10 flex flex-col h-full p-10">
          {/* Top - Glide Logo & Brand */}
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                boxShadow: '0 8px 32px rgba(59,130,246,0.3)'
              }}
            >
              <Layers className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight">
                Glide
              </h1>
              <p className="text-xs text-slate-400 font-medium">
                Asset Management Platform
              </p>
            </div>
          </div>

          {/* Middle - Glide Features */}
          <div className="flex-1 flex flex-col justify-center max-w-md">
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">
              Enterprise Asset Management Made Simple
            </h2>
            <p className="text-slate-400 text-base mb-8">
              Track, manage, and optimize your organization's assets with powerful tools designed for modern enterprises.
            </p>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {features.slice(0, 4).map((feature, idx) => {
                const FeatureIcon = feature.icon;
                const isActive = idx === currentFeature % 4;
                return (
                  <div 
                    key={idx}
                    className={`p-3 rounded-xl border transition-all duration-500 cursor-default ${
                      isActive 
                        ? 'bg-white/10 border-blue-500/50 scale-[1.02]' 
                        : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 transition-all duration-500 ${
                      isActive 
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600' 
                        : 'bg-slate-800'
                    }`}>
                      <FeatureIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    </div>
                    <h4 className="text-white font-semibold text-sm mb-0.5">{feature.title}</h4>
                    <p className="text-slate-500 text-xs leading-relaxed">{feature.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Progress Dots */}
            <div className="flex gap-2">
              {features.slice(0, 4).map((_, idx) => (
                <div 
                  key={idx}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    idx === currentFeature % 4 ? 'w-8 bg-blue-500' : 'w-2 bg-slate-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Bottom - Glide Info */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-slate-400 text-sm">System Online</span>
            </div>
            <div className="text-slate-600">•</div>
            <span className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Glide Platform
            </span>
          </div>
        </div>
      </div>

      {/* Right Panel - Customer Organization Branding + Login Form */}
      <div 
        className="flex-1 flex flex-col lg:rounded-l-[2.5rem] relative overflow-hidden"
        style={{
          background: branding?.loginBackground 
            ? `url(${getImageUrl(branding.loginBackground)}) center/cover no-repeat`
            : 'white'
        }}
      >
        {/* Background Overlay for custom backgrounds */}
        {branding?.loginBackground && (
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${branding.loginOverlayColor || 'rgba(255,255,255,0.95)'} 0%, rgba(255,255,255,0.98) 100%)`,
              zIndex: 1
            }}
          />
        )}

        {/* Subtle Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.02) 1px, transparent 0)`,
            backgroundSize: '24px 24px',
            zIndex: 2
          }}
        />

        {/* Mobile Header - Glide branding on mobile */}
        <div className="lg:hidden p-6 pb-0 relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)' }}
            >
              <Layers className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Glide</h1>
              <p className="text-xs text-slate-500">Asset Management</p>
            </div>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-8 relative z-10">
          <div className="w-full max-w-md mx-auto">
            {/* Organization Branding */}
            <div className="text-center mb-8">
              {branding?.logo ? (
                <img 
                  src={getImageUrl(branding.logo)} 
                  alt={branding?.name || 'Organization'} 
                  className="h-16 max-w-[280px] mx-auto mb-4 object-contain"
                />
              ) : branding?.name ? (
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Building2 className="w-12 h-12 text-blue-600" />
                  <span className="text-2xl font-bold text-slate-900">{branding.name}</span>
                </div>
              ) : null}
              
              {branding?.tagline && (
                <p className="text-slate-500 text-sm italic">{branding.tagline}</p>
              )}
            </div>

            {/* Welcome Text */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {branding?.loginWelcomeMessage || 'Welcome back'}
              </h2>
              <p className="text-slate-500">
                Sign in to access your asset management portal
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-5">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <User className={`w-5 h-5 transition-colors ${
                      focusedField === 'email' ? 'text-blue-600' : 'text-slate-400'
                    }`} />
                  </div>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    autoComplete="email"
                    autoFocus
                    className={`w-full h-12 pl-11 pr-4 text-sm border-2 rounded-xl outline-none transition-all duration-200 bg-white ${
                      focusedField === 'email' 
                        ? 'border-blue-500 ring-4 ring-blue-500/10' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    {...loginForm.register('email')}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Lock className={`w-5 h-5 transition-colors ${
                      focusedField === 'password' ? 'text-blue-600' : 'text-slate-400'
                    }`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className={`w-full h-12 pl-11 pr-12 text-sm border-2 rounded-xl outline-none transition-all duration-200 bg-white ${
                      focusedField === 'password' 
                        ? 'border-blue-500 ring-4 ring-blue-500/10' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    {...loginForm.register('password')}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Forgot password link */}
              <div className="flex justify-end">
                <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  Forgot password?
                </a>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full h-12 flex items-center justify-center gap-2 text-white font-semibold rounded-xl transition-all duration-200 ${
                  isLoading 
                    ? 'bg-slate-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98]'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Help Text */}
            <p className="text-center text-sm text-slate-500 mt-6">
              Don't have an organization?{' '}
              <a href="/register" className="text-blue-600 font-medium hover:underline">Create one</a>
            </p>
          </div>
        </div>

        {/* Bottom Security Badge */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white/80 backdrop-blur-sm relative z-10">
          <div className="max-w-md mx-auto">
            {/* Organization footer text (address) */}
            {branding?.footerText && (
              <p className="text-xs text-slate-500 text-center mb-3 leading-relaxed">
                {branding.footerText}
              </p>
            )}
            <div className="flex items-center justify-between text-xs text-slate-400">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-green-500" />
                  <span>Secure Login</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>Enterprise Grade</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span>Powered by</span>
                <span className="font-semibold text-slate-600">Glide</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
